import { MouseEventHandler, useCallback } from 'react';

import { EducationalMaterial } from '@dreryk/egabinet-knowledge-base-frontend-api';

import { dispatchSnackbarNegativeEvent, StyledFlex } from '@dreryk/react-components';
import { createTranslatedText, useOpenInNewTab } from '@dreryk/react-common';

import { dashboardTranslationKeys } from '../../translations';

import {
   StyledAuthors,
   StyledContent,
   StyledEducationalMaterialCard,
   StyledHeading,
   StyledImage,
} from './EducationalMaterialCard.styled';
import { EducationalMaterialMessage } from './EducationalMaterialStatus';
import { useEducationalMaterialDashboardOperationRegisterCommand } from './EducationalMaterialCard.hooks';

type EducationalMaterialCardProps = {
   educationalMaterial: EducationalMaterial;
};

export const EducationalMaterialCard = (props: EducationalMaterialCardProps): JSX.Element => {
   const { openInNewTab } = useOpenInNewTab();

   const {
      executeAsync: registerEducationalMaterialOperation,
   } = useEducationalMaterialDashboardOperationRegisterCommand(props.educationalMaterial);

   const handleOpenInNewTab = useCallback<MouseEventHandler<HTMLElement>>(
      (event) => {
         event.stopPropagation();
         openInNewTab(props.educationalMaterial.contentUrl)
            .then(registerEducationalMaterialOperation)
            .catch(() => dispatchSnackbarNegativeEvent(dashboardTranslationKeys.educationalMaterialOpenError));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [openInNewTab, props.educationalMaterial.contentUrl]
   );

   return (
      <StyledEducationalMaterialCard onClick={handleOpenInNewTab}>
         <StyledImage
            src={props.educationalMaterial.thumbnailUrl}
            alt={props.educationalMaterial.name}
            loading={'lazy'}
         />
         <StyledContent gap={'12px'} direction={'column'}>
            <StyledFlex gap={'4px'} direction={'column'}>
               <StyledHeading
                  testId={'title'}
                  type={'h2'}
                  content={createTranslatedText(props.educationalMaterial.name)}
                  margin={0}
                  bold
               />
               <StyledAuthors>{props.educationalMaterial.authors.join(', ')}</StyledAuthors>
            </StyledFlex>

            <StyledFlex justifyContent={'space-between'} alignItems={'center'}>
               <EducationalMaterialMessage category={props.educationalMaterial.categories?.at(0)} />
            </StyledFlex>
         </StyledContent>
      </StyledEducationalMaterialCard>
   );
};
